/**
 * FieldSync Multi-Company Configuration Loader
 *
 * Detects which company's Firebase project to connect to based on:
 * 1. Subdomain (company.fieldsync.solutions)
 * 2. URL parameter (?project=company)
 * 3. localStorage (last selected company)
 * 4. Fallback to default config.js (single-company mode)
 */

import { firebaseConfig as defaultConfig } from './config';

// Defensive: ensure we have a valid config import
const rawConfig = defaultConfig || {};

/**
 * Project Registry — Maps company identifiers to Firebase configs.
 *
 * This is the static registry for known companies. In production, you may
 * want to fetch this from a lightweight meta API or CDN instead.
 *
 * Each entry contains:
 * - Firebase config (apiKey, projectId, etc.)
 * - Company branding (name, logo, colors)
 * - Zoho credentials (orgId, datacenter)
 */
const PROJECT_REGISTRY = {
  // Example: City Surface Repairs (existing — replace with real credentials)
  'citysurface': {
    // Firebase config (pulled from default config for same-project mode)
    apiKey: rawConfig.apiKey,
    authDomain: rawConfig.authDomain,
    projectId: rawConfig.projectId,
    storageBucket: rawConfig.storageBucket,
    messagingSenderId: rawConfig.messagingSenderId,
    appId: rawConfig.appId,
    measurementId: rawConfig.measurementId,
    // Company branding
    companyName: 'City Surface Repairs Ltd',
    companySlug: 'citysurface',
    // Zoho credentials (company-specific)
    zohoOrgId: rawConfig.zohoOrgId,
    zohoClientId: rawConfig.zohoClientId,
    zohoDatacenter: rawConfig.zohoDatacenter || 'eu',
    zohoRedirectUri: rawConfig.zohoRedirectUri,
    zohoScopes: rawConfig.zohoScopes,
    zohoToken: rawConfig.zohoToken,
    // Cloud Functions
    zohoFunctionsRegion: rawConfig.zohoFunctionsRegion || 'us-central1',
  },
  // Add more companies here as they are provisioned:
  // 'acme-construction': {
  //   apiKey: '...',
  //   projectId: 'fieldsync-acme-construction',
  //   companyName: 'Acme Construction Ltd',
  //   zohoOrgId: '...',
  //   ...
  // },
};

/**
 * Determine which project to load based on detection strategy.
 * Returns the company slug, or null for single-company fallback mode.
 */
export const getProjectSlug = () => {
  // Strategy 1: Check URL parameter (?project=citysurface)
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const projectParam = params.get('project');
    if (projectParam && PROJECT_REGISTRY[projectParam]) {
      return projectParam;
    }
  }

  // Strategy 2: Check subdomain (citysurface.fieldsync.solutions)
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const parts = host.split('.');

    // Handle subdomains: citysurface.fieldsync.solutions → citysurface
    // Exclude www, login, and root domains
    if (parts.length >= 3) {
      const subdomain = parts[0];
      const isKnownSubdomain = subdomain !== 'www' &&
                                subdomain !== 'login' &&
                                subdomain !== 'app' &&
                                subdomain !== 'fieldsync' &&
                                subdomain !== 'localhost';

      if (isKnownSubdomain && PROJECT_REGISTRY[subdomain]) {
        return subdomain;
      }
    }
  }

  // Strategy 3: Check localStorage for last selected company
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('fieldsync_project_slug');
    if (stored && PROJECT_REGISTRY[stored]) {
      return stored;
    }
  }

  // Fallback: null means use default config (single-company mode)
  return null;
};

/**
 * Save the selected company slug to localStorage for future visits.
 */
export const saveProjectSlug = (slug) => {
  if (typeof window !== 'undefined' && slug) {
    localStorage.setItem('fieldsync_project_slug', slug);
  }
};

/**
 * Clear the saved company slug.
 */
export const clearProjectSlug = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('fieldsync_project_slug');
  }
};

/**
 * Get the full Firebase + company config for the current company/project.
 * Returns merged config with default fallback.
 *
 * NOTE: In single-company fallback mode, we return the raw defaultConfig
 * without extra keys to ensure backward compatibility with initializeApp().
 */
export const getProjectConfig = () => {
  const slug = getProjectSlug();

  if (!slug) {
    // Single-company fallback mode: use config.js as-is for max compatibility
    return {
      ...rawConfig,
      companyName: rawConfig.companyName || 'Field Sync',
      companySlug: 'default',
      isMultiProjectMode: false,
    };
  }

  const project = PROJECT_REGISTRY[slug];
  if (!project) {
    console.warn(`[ConfigLoader] Unknown project slug: "${slug}". Falling back to default.`);
    return {
      ...rawConfig,
      companyName: rawConfig.companyName || 'Field Sync',
      companySlug: 'default',
      isMultiProjectMode: false,
    };
  }

  // Return merged config with multi-project flag
  return {
    ...project,
    isMultiProjectMode: true,
  };
};

/**
 * Get a list of all registered companies (for admin selection UI).
 */
export const getRegisteredCompanies = () => {
  return Object.entries(PROJECT_REGISTRY).map(([slug, config]) => ({
    slug,
    companyName: config.companyName || slug,
    projectId: config.projectId,
  }));
};

/**
 * Register a new company in the registry (call after provisioning).
 * This is a runtime registration — for persistence, update the registry source.
 */
export const registerCompany = (slug, config) => {
  PROJECT_REGISTRY[slug] = config;
};

/**
 * Check if running in multi-project (multi-company) mode.
 */
export const isMultiProjectMode = () => {
  return !!getProjectSlug();
};

/**
 * Export the raw registry for advanced use cases.
 */
export { PROJECT_REGISTRY };
